import YesNoChoices from "../../../data/yes-no-choice";
import { thoughtworksApiUserGuideUrl } from "../../../data/urls";
import { replaceInvalidGitHubRepoNameCharactersWithHyphens } from "../../../utils/process-fields";
import {
  getGcpProjectNameValidatorConfig,
  getGcpValidatorConfig,
  getGithubRepoNameValidatorConfig,
  getGithubReposValidatorConfig,
} from "../../../utils/validator";
import { sensibleDefaultBadgeHtml } from "../../../../../common/components/sensible-default-badge";

const githubOrgVisibleIf = (yes: string) => `{isCodeRepositoryNeeded}='${yes}'`;
const engineeringPlatformSupportEmail = "engineering-platform-support@thoughtworks.com";
const noGithubOrgMappedMessage = "No GitHub organization mapped to the team, please send email to "
  + `<a href="mailto:${engineeringPlatformSupportEmail}">${engineeringPlatformSupportEmail}</a>`;

/** `null` = BFF resolution still in flight; do not treat as “no orgs mapped”. */
const getGitsGithubOrganisationFormElement = (
  gitsGithubOrganisationChoices: string[] | null,
  yes: string,
) => {
  if (gitsGithubOrganisationChoices === null) {
    return {
      type: "html" as const,
      name: "githubOrganisationLoading",
      visibleIf: githubOrgVisibleIf(yes),
      html: "<div class=\"gits-infra-github-org-loading\">Loading GitHub organisations…</div>",
    };
  }
  if (gitsGithubOrganisationChoices.length === 0) {
    return {
      type: "html" as const,
      name: "githubOrganisationMappingError",
      visibleIf: githubOrgVisibleIf(yes),
      html: `<div role="alert" class="gits-infra-org-mapping-error-msg">${noGithubOrgMappedMessage}</div>`,
    };
  }
  if (gitsGithubOrganisationChoices.length > 1) {
    return {
      type: "dropdown" as const,
      name: "githubOrganisation",
      title: "GitHub organisation",
      visibleIf: githubOrgVisibleIf(yes),
      isRequired: true,
      choices: gitsGithubOrganisationChoices,
    };
  }
  return {
    type: "text" as const,
    name: "githubOrganisation",
    title: "GitHub organisation",
    visibleIf: githubOrgVisibleIf(yes),
    isRequired: true,
    readOnly: true,
    defaultValue: gitsGithubOrganisationChoices[0] ?? "",
  };
};

const getInfraConfigQuestions = (
  applicationName: string,
  gitsGithubOrganisationChoices: string[] | null,
) => {
  const githubRepoPrefix = replaceInvalidGitHubRepoNameCharactersWithHyphens(applicationName);
  const hasGithubOrganisationMapped = gitsGithubOrganisationChoices !== null
    && gitsGithubOrganisationChoices.length > 0;
  const githubRepoFlowVisibleIf = `{isCodeRepositoryNeeded}='${YesNoChoices.YES}' and ${hasGithubOrganisationMapped}`;
  const codeRepositoryPanel = {
    type: "panel",
    title: "Code Repository",
    elements: [
      {
        type: "radiogroup",
        name: "isCodeRepositoryNeeded",
        title: "Do you need GitHub repository?",
        isRequired: true,
        colCount: 1,
        choices: [
          YesNoChoices.YES,
          YesNoChoices.NO,
        ],
      },
      getGitsGithubOrganisationFormElement(
        gitsGithubOrganisationChoices,
        YesNoChoices.YES,
      ),
      {
        type: "matrixdynamic",
        minRowCount: 1,
        maxRowCount: 5,
        rowCount: 1,
        width: "max-content",
        name: "githubRepos",
        title: "Repository names (upto 5 repositories)",
        description: "Repositories are created in the GitHub organisation you selected above. "
          + `Full name will be: ${githubRepoPrefix}-<your suffix>.`,
        addRowText: "+ Add Repo",
        removeRowText: "\t",
        visibleIf: githubRepoFlowVisibleIf,
        columns: [
          {
            name: "name",
            width: 600,
            description: `pre--${githubRepoPrefix}-`,
            cellType: "text",
            validators: getGithubRepoNameValidatorConfig(),
          },
          {
            name: "dummy-field",
            description: "This is needed for github repo to allow empty values",
            defaultValue: "dummy-value",
            visible: "disabled",
          },
        ],
        validators: getGithubReposValidatorConfig(),
      },
      {
        type: "radiogroup",
        name: "isCiCdPipelineNeeded",
        title: `Do you need CI/CD pipeline? (CircleCI${sensibleDefaultBadgeHtml})`,
        colCount: 1,
        choices: [
          YesNoChoices.YES,
          YesNoChoices.NO,
        ],
        visibleIf: githubRepoFlowVisibleIf,
        isRequired: true,
      },
    ],
  };
  return [
    codeRepositoryPanel,
    {
      type: "panel",
      title: "Cloud Provider",
      elements: [
        {
          type: "radiogroup",
          name: "isHostingServiceNeeded",
          title: "Do you need GCP project?",
          choices: [
            YesNoChoices.YES,
            YesNoChoices.NO,
          ],
          isRequired: true,
        },
        {
          type: "matrixdynamic",
          minRowCount: 1,
          maxRowCount: 5,
          rowCount: 1,
          width: "max-content",
          name: "gcpProjects",
          title: "GCP project names (upto 5 projects)",
          description: "Use the word \"prod\" if the project is for a production "
            + "environment. E.g. app-name-prod.",
          addRowText: "+ Add Project",
          removeRowText: "\t",
          visibleIf: `{isHostingServiceNeeded}='${YesNoChoices.YES}'`,
          columns: [
            {
              name: "name",
              width: 600,
              cellType: "text",
              isRequired: true,
              requiredErrorText: "Required",
              defaultValue: applicationName,
              validators: getGcpProjectNameValidatorConfig(),
            },
          ],
          validators: getGcpValidatorConfig(),
        },
      ],
    },
    {
      type: "panel",
      title: "Other requirements",
      elements: [
        {
          type: "comment",
          name: "comment",
          title: "Describe your requirement, if you didn't find what you are looking for.",
        },
      ],
      visibleIf: `{isCodeRepositoryNeeded}='${YesNoChoices.NO}' and {isHostingServiceNeeded}='${YesNoChoices.NO}'`,
    },
    {
      type: "html",
      html: "<div>Please follow the instructions on the "
        + `<a class="tw-api-url" href="${thoughtworksApiUserGuideUrl}" target="_blank">API User Guide</a>, `
        + "if you are looking for APIs of Thoughtworks systems.</div>",
      visibleIf: `{isCodeRepositoryNeeded}='${YesNoChoices.NO}' and {isHostingServiceNeeded}='${YesNoChoices.NO}'`,
    },
  ];
};

export default getInfraConfigQuestions;
